// 〈全部站台〉〈站台詳情〉與站台機密（批151 C6／C7）——對應後端 `app/api/admin_sites.py`
// 與 `app/api/admin_site_secrets.py`（同一個 `/api/admin` prefix）。
//
// ★ **只有我方員工的 realm**：這幾支端點不在代理商 sub-router 裡（後端 fail-closed），
//   所以本檔一律打 `/api/admin/...`，不經 `api/hosting.ts::base()` 的分流。
//
// 權限（後端是真閘門；畫面只是照著藏按鈕）：
// - 清單、站台詳情：operator 以上（詳情的授權／子證／站台機密三塊 admin 以上，operator 拿到 null）
// - 同期限改內容：admin＋政策閘（內容偏離套裝要營運主管，403 `role_required`）
// - 站台機密的設定／更換／清除／重送／反查：營運主管、網頁專屬；admin 只看狀態（**永遠沒有值**）

import { api, qs } from './client'
import type { HostJob, HostSite } from './hosting'
import type { LicenseAddon, MetricDefinition } from './ls'

/** 清單列＝主機詳情的站台列（共用投影 `_site_out`）＋本端點多帶的主機欄位。 */
export interface SiteListRow extends HostSite {
  host_name: string | null
  host_status: string | null
  /** 主機屬於哪家代理商（null＝我方自營）。 */
  host_reseller_id: string | null
  host_reseller_name: string | null
}

export interface SiteListPage {
  items: SiteListRow[]
  next_cursor: string | null
}

export interface SiteListQuery {
  product?: string
  customer_id?: string
  host_id?: string
  /** 只看某個狀態（`decommissioned`＝只看已回收）。 */
  state?: string
  /** 連已回收的站一起列（預設不列）。 */
  include_decommissioned?: boolean
  /** 站台代號或網域的關鍵字（不分大小寫）。 */
  q?: string
  limit?: number
  cursor?: string
}

export const listSites = (q: SiteListQuery = {}) =>
  api<SiteListPage>(
    `/api/admin/sites${qs({
      product: q.product || undefined,
      customer_id: q.customer_id || undefined,
      host_id: q.host_id || undefined,
      state: q.state || undefined,
      include_decommissioned: q.include_decommissioned ? 'true' : undefined,
      q: q.q?.trim() || undefined,
      limit: q.limit != null ? String(q.limit) : undefined,
      cursor: q.cursor || undefined,
    })}`,
  )

/** 站台詳情的主機摘要（主機詳情那一份太大，這裡只有標頭要的）。 */
export interface SiteHostBrief {
  id: string
  name: string
  fqdn: string | null
  public_ip: string | null
  status: string
  agent_kind: string | null
  agent_version: string | null
  last_seen: string | null
  /** null＝我方自營；非 null＝代理商自備的主機。 */
  reseller_id: string | null
  reseller_name: string | null
}

/** 站台詳情的「目前生效的證」（摘要投影，**不含 blob**）。 */
export interface SiteLicense {
  license_id: string
  product: string
  status: string
  modules: Record<string, string>
  plan: string | null
  plan_label: string | null
  quotas: Record<string, number | null> | null
  policy: Record<string, unknown>
  expires_at: string | null
  not_before: string | null
  term_days: number | null
  term_minutes: number
  deployment_mode: string
  machine_fingerprint: Record<string, string> | null
  host_instance_id: string | null
  created_at: string
  issued_via: string | null
  trial: boolean
  supersedes: string | null
}

/** 子證列：`addon_view` 的形狀＋「現在是否生效中」（未到啟用時間／已到期＝false）。 */
export interface SiteAddon extends LicenseAddon {
  in_effect: boolean
}

/** 合併後的模組／配額（後端已依產品宣告的合併方式算好，前端直接顯示）。 */
export interface SiteEffective {
  effective_modules: Record<string, string>
  effective_quotas: Record<string, number | null> | null
  /** 哪些鍵被子證改變，以及貢獻它的子證裡最早的到期時刻（null＝尚未蓋章）。 */
  addon_sources: {
    quotas?: Record<string, string | null>
    modules?: Record<string, string | null>
  }
}

/** 站台機密的一個欄位狀態（**永遠沒有值**）。 */
export interface SiteSecretField {
  key: string
  env: string
  label: string
  hint: string | null
  /** false＝產品宣告已拿掉、但值還存著（殘留列，只能清除）。 */
  declared: boolean
  configured: boolean
  /** 末四碼；值太短時不顯示（null）。 */
  last4: string | null
  /** 現行版本（毫秒時間戳）。 */
  version: number | null
  /** agent 回報已寫進站台的版本。 */
  applied_version: number | null
  applied_at: string | null
  /** 現行版本已套用到站台。 */
  applied: boolean
  source: string | null
  set_by: string | null
  set_at: string | null
}

export interface SiteSecretsStatus {
  site_id: string
  slug: string
  product: string
  state: string
  /** 這個產品有沒有宣告任何站台機密欄位。 */
  declared: boolean
  /** 授權中心有沒有設定加密主鑰（沒有＝寫入一律 503）。 */
  secret_key_available: boolean
  /** 這台主機的 agent 支不支援站台機密。 */
  agent_supported: boolean
  /** 我方自營主機（代理商主機不可放我方的機密）。 */
  vendor_host: boolean
  /** 站台狀態允許派工（運作中／停用中，且沒有進行中的開站任務）。 */
  can_dispatch: boolean
  fields: SiteSecretField[]
}

export interface SiteDetail {
  site: HostSite
  host: SiteHostBrief | null
  customer: { id: string; name: string } | null
  /** 部署回報的泛型指標（null＝從沒回報過）。 */
  metrics: Record<string, number> | null
  metrics_at: string | null
  usage: {
    seats_in_use: number | null
    partner_seats_in_use: number | null
    quotas_in_use: Record<string, number> | null
    last_seen: string
  } | null
  metric_definitions: MetricDefinition[]
  /** 以下四塊 admin 以上才有值；operator 拿到 null（`sections` 標明是權限而不是沒資料）。 */
  license: SiteLicense | null
  addons: SiteAddon[] | null
  effective: SiteEffective | null
  site_secrets: SiteSecretsStatus | null
  sections: { license: boolean; addons: boolean; site_secrets: boolean }
}

export const getSite = (siteId: string) => api<SiteDetail>(`/api/admin/sites/${siteId}`)

/** 同期限改內容：三欄至少給一欄，給了的那一欄**整包取代**；期限原封不動。 */
export interface SiteLicenseContentIn {
  modules?: Record<string, string>
  quotas?: Record<string, number | null>
  policy?: Record<string, unknown>
  note?: string | null
}

export interface SiteLicenseContentResult {
  site_id: string
  license: SiteLicense
  superseded: string
  /** 真的變了的欄：`{modules|quotas|policy: {from, to}}`（只留變了的）。 */
  changes: Record<string, { from: unknown; to: unknown }>
  policy: string
  policy_reason: string | null
  issued_via: string
  unknown_modules?: string[]
  unknown_module_items?: string[]
  unknown_quota_keys?: string[]
}

export const changeSiteLicenseContent = (siteId: string, body: SiteLicenseContentIn) =>
  api<SiteLicenseContentResult>(`/api/admin/sites/${siteId}/license-content`, {
    method: 'POST',
    body,
  })

// --- 站台機密 ---------------------------------------------------------------

/** 寫入類端點的回應：值沒變＝`changed: false`、`job: null`（不派工）。 */
export interface SiteSecretWriteResult {
  changed: boolean
  job: HostJob | null
  secrets: SiteSecretsStatus
}

export const getSiteSecrets = (siteId: string) =>
  api<SiteSecretsStatus>(`/api/admin/sites/${siteId}/secrets`)

/** 設定或更換（營運主管）。值只在這一次請求裡出現，畫面不保留、不回填。 */
export const putSiteSecret = (siteId: string, key: string, value: string) =>
  api<SiteSecretWriteResult>(`/api/admin/sites/${siteId}/secrets/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: { value },
  })

/**
 * 清除＝立刻刪除並釋出（同一把值可以給別站），再派工把它從站台拿掉。
 *
 * 裁示 126①：欄位已宣告、但授權中心沒有紀錄（例如開站失敗時已釋出）時，同一支端點會**再派一次
 * clear**，把站台 `.env` 裡可能殘留的那一行刪掉（畫面上的「清除站上殘留」）。
 */
export const deleteSiteSecret = (siteId: string, key: string) =>
  api<SiteSecretWriteResult>(`/api/admin/sites/${siteId}/secrets/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  })

/**
 * 只釋出的回應：沒有派工（`job` 恆不存在）；成功時 `changed` 恆為 `true`。
 * ⚠️ 授權中心本來就沒有紀錄**不是** `changed: false`，而是 404 `secret_not_set`
 *   （後端 `services/site_secrets.release_secret`）——呼叫端要在錯誤路徑處理。
 */
export interface SiteSecretReleaseResult {
  changed: boolean
  secrets: SiteSecretsStatus
}

/**
 * 只釋出、不派工（營運主管、網頁專屬；裁示 126②）：只在授權中心刪除紀錄並釋出唯一性，
 * **站台上的值不會被移除**。用在開站卡住、或站台已不再使用時（例如回收任務卡住、站台機器已不在）。
 */
export const releaseSiteSecret = (siteId: string, key: string) =>
  api<SiteSecretReleaseResult>(
    `/api/admin/sites/${siteId}/secrets/${encodeURIComponent(key)}/release`,
    { method: 'POST' },
  )

/** 重送：`keys` 省略＝全部已設定的欄位。 */
export const redeliverSiteSecrets = (siteId: string, keys?: string[]) =>
  api<SiteSecretWriteResult>(`/api/admin/sites/${siteId}/secrets/redeliver`, {
    method: 'POST',
    body: keys && keys.length ? { keys } : {},
  })

export interface SiteSecretMatch {
  site_id: string
  slug: string
  domain: string | null
  product: string
  state: string
  host_id: string
  host_name: string | null
  customer_id: string | null
  customer_name: string | null
  key: string
  env: string
  last4: string | null
  version: number
  set_at: string | null
}

export interface SiteSecretLookupResult {
  mode: 'value' | 'last4'
  matches: SiteSecretMatch[]
}

/** 反查（營運主管）：body 是 `{value}` 或 `{last4}` 其中一個（見 `lib/siteSecrets.ts::lookupBody`）。 */
export const lookupSiteSecret = (body: { value: string } | { last4: string }) =>
  api<SiteSecretLookupResult>('/api/admin/site-secrets/lookup', { method: 'POST', body })
